/**
 * Pure calculation logic for the energy cost & solar savings calculator
 * (content/pages/energy-cost-calculator.md). Kept separate from the DOM
 * wiring in main.ts so the numbers themselves can be unit tested directly,
 * the same pattern installers-data.ts uses for matchTopInstallers.
 *
 * Every constant here is a deliberately simple, sourced approximation, not
 * an MCS/SAP-grade calculation, see the calculator page's own disclaimer.
 */

import { isKnownUkPostcodeArea, regionForPostcode, type UkRegion } from "./postcode-region.ts";

export type HouseholdSize = "small" | "medium" | "large";
export type CalcMode = "usage" | "bill";

/**
 * sessionStorage key main.ts writes the last computed selfConsumptionShare under, so
 * other tools on the site (the EV charging comparison) can personalize a "with solar"
 * figure for a returning visitor instead of falling back to the site-wide default.
 *
 * Deliberately not savingPct: that's the percentage of the household's bill offset
 * (which factors in export income at a different rate, and is capped at 100), a
 * different quantity from "share of solar generation that's self-used" the EV tool's
 * blended rate actually needs. An earlier version of this stored savingPct here, which
 * for most household sizes sits at or near its 100% cap regardless of usage entered,
 * so the EV tool ended up always assuming ~100% self-consumption instead of the real,
 * usually-much-lower figure.
 */
export const SELF_CONSUMPTION_SHARE_STORAGE_KEY = "energyCalcSelfConsumptionShare";

/** Matches Ofgem's typical domestic consumption values for electricity (single-rate meters), revised 1 July 2026, cited in content/pages/glossary.md's kWh entry. */
export const USAGE_BAND_KWH: Record<HouseholdSize, number> = {
  small: 1600,
  medium: 2500,
  large: 3800,
};

/** Below this, a system isn't really worth installing, regardless of household size. */
export const MIN_SYSTEM_KW = 2;

/**
 * How much roof a typical home of each size actually has to put panels on, in kWp.
 * This is what makes household size and entered usage/bill work together instead of
 * one silently overriding the other: usage decides how big a system would be worth
 * having, this decides how big a system could actually fit. A 1-2 person household in
 * a small property with an unusually high bill (a home office running heavy equipment,
 * say) still won't fit a 24.5kWp system on its roof, so the estimate caps there instead
 * of recommending something unbuildable.
 *
 * "large" is set so its saving holds up to roughly £4,000/year of declared spend before
 * it flattens out (raised twice: once from an original ~£1,650 plateau, then again from
 * ~£2,100, both of which caught realistic high-usage households, EV plus heat pump
 * territory, and read as the tool being broken rather than "you've maxed out your
 * roof"). A 24.5kWp system is a big, three-phase-supply-territory install, well past
 * "typical" for a single house, so above this a real quote is genuinely the next step
 * rather than a bigger number from a ballpark tool, which is what the calculator's own
 * note says. "medium" is set the same way, its own plateau raised from ~£1,650 to
 * ~£2,200, and "small" from ~£820 to ~£1,600.
 */
export const ROOF_MAX_KW: Record<HouseholdSize, number> = {
  small: 9.7,
  medium: 13.5,
  large: 24.5,
};

/**
 * Recommended system size scales with annual usage, at this many kWp per kWh/year,
 * before the roof-space cap above is applied. Calibrated so a "large" household's own
 * typical usage lands on a 6kWp system, the top of the "4 to 6kW system" range
 * content/pages/solar.md quotes.
 *
 * An earlier version scaled each household-size band by its own separate ratio
 * (a fixed SYSTEM_SIZE_KW[size] against USAGE_BAND_KWH[size]). Those per-band ratios
 * weren't equal, so entering the exact same known usage/bill figure recommended a
 * different system depending on which household-size button was still selected,
 * sometimes even a smaller system for "large" than for "small". Using one ratio for
 * every band means the *before-the-roof-cap* figure depends only on actual usage,
 * never on that leftover selection; ROOF_MAX_KW is the one place household size still
 * has a say, and only when usage would call for more roof than that size plausibly has.
 */
export const KWP_PER_KWH_USAGE = 6 / USAGE_BAND_KWH.large;

/** Exported so callers (tests, mainly) can round a raw ROOF_MAX_KW figure the same way computeEnergyEstimate does, since a cap that isn't itself a 0.5 multiple (9.7, chosen to hit a specific plateau point) still gets rounded once it's the binding constraint. */
export function roundToHalfKw(kw: number): number {
  return Math.round(kw * 2) / 2;
}

/** Default system size for each household size band, shown as-is when no usage/bill figure is entered. Derived from KWP_PER_KWH_USAGE and ROOF_MAX_KW so it's consistent with whatever a custom figure produces. */
export const SYSTEM_SIZE_KW: Record<HouseholdSize, number> = {
  small: roundToHalfKw(Math.min(USAGE_BAND_KWH.small * KWP_PER_KWH_USAGE, ROOF_MAX_KW.small)),
  medium: roundToHalfKw(Math.min(USAGE_BAND_KWH.medium * KWP_PER_KWH_USAGE, ROOF_MAX_KW.medium)),
  large: roundToHalfKw(Math.min(USAGE_BAND_KWH.large * KWP_PER_KWH_USAGE, ROOF_MAX_KW.large)),
};

/** UK-wide average solar yield in kWh generated per kWp installed per year, before the regional adjustment below (MCS's reference figure for a well-sited UK system, which runs up to ~1,050 kWh/kWp with a good south-facing tilt). */
export const BASE_YIELD_KWH_PER_KWP = 950;

/** Approximates how much more/less sun each UK region gets (south east England sunniest, Scotland least), applied to BASE_YIELD_KWH_PER_KWP. Calibrated so Scotland lands in the ~850-900 kWh/kWp range current published regional figures suggest, rather than well under it. A real quote uses a proper irradiance calc for the actual roof. */
export const REGION_YIELD_MULTIPLIER: Record<UkRegion, number> = {
  "south-england": 1.12,
  midlands: 1.05,
  wales: 1.02,
  "north-england": 0.97,
  "northern-ireland": 0.95,
  scotland: 0.92,
  other: 1,
};
/** Used when no postcode is entered, or it doesn't resolve to a known area: a UK-wide average. */
export const DEFAULT_YIELD_MULTIPLIER = 0.95;

/** Pence per kWh. Matches the Ofgem price cap's standard unit rate (26.11p, Jul-Sep 2026) and HOME_STANDARD_RATE_PENCE in ev-charging-cost-calculator.ts. */
export const UNIT_RATE_PENCE = 26;
/** Pence per kWh exported. The average SEG payment households actually receive is closer to 13p than the 15p top-of-market rate (best available tariffs run 12-15p); 13p keeps this a "typical" figure rather than a best-case one. */
export const SEG_RATE_PENCE = 13;
/** Share of solar generation a household without a battery typically uses directly rather than exporting, a commonly-cited rule of thumb. */
export const SELF_CONSUMPTION_SHARE = 0.4;

export interface EnergyEstimateInput {
  size: HouseholdSize;
  mode: CalcMode;
  /** Raw value from the "know your numbers" field: kWh if mode is "usage", pounds if mode is "bill". Null/absent falls back to the household size's Ofgem band. */
  knownValue?: number | null;
  /** Raw postcode string, validated internally; an unrecognised or blank postcode falls back to DEFAULT_YIELD_MULTIPLIER. */
  postcode?: string | null;
}

export interface EnergyEstimate {
  annualKwh: number;
  annualCost: number;
  systemKw: number;
  generationKwh: number;
  selfUsedKwh: number;
  exportedKwh: number;
  totalSaving: number;
  /**
   * Share of the household's own annual usage that solar covers directly (0-100).
   * Deliberately self-use only, not (self-use + export income) / bill: that blended
   * version can climb past 100% for a well-sized system (export income is real money,
   * but it isn't "your bill", it's a separate income stream that just happens to be
   * generous enough to more than cover the gap), and reads as "solar pays your whole
   * bill and then some" when what's actually happening is more modest, your usage is
   * partly met directly and the rest still comes from the grid. Self-used kWh can
   * never exceed annual usage by construction, so this needs no artificial cap.
   */
  savingPct: number;
  /** Share of this household's own generation that gets self-used rather than exported (0-1). Usually exactly SELF_CONSUMPTION_SHARE, but comes out lower when usage is low enough that annualKwh, not generation, is the binding cap on selfUsedKwh below. This is the figure the EV charging comparison's "with your solar" column actually wants. */
  selfConsumptionShare: number;
  /** True when usage alone would call for a bigger system than ROOF_MAX_KW[size] allows, i.e. the household-size selection, not the entered usage/bill, is what's holding systemKw back. Lets the UI explain why a big declared bill on a "small" household doesn't translate into an equally big system. */
  roofCapped: boolean;
}

function yieldMultiplierForPostcode(postcode?: string | null): number {
  const trimmed = postcode?.trim() ?? "";
  if (trimmed === "" || !isKnownUkPostcodeArea(trimmed)) return DEFAULT_YIELD_MULTIPLIER;
  const region = regionForPostcode(trimmed);
  return region ? REGION_YIELD_MULTIPLIER[region] : DEFAULT_YIELD_MULTIPLIER;
}

export function computeEnergyEstimate(input: EnergyEstimateInput): EnergyEstimate {
  const { size, mode, knownValue, postcode } = input;

  let annualKwh: number;
  if (knownValue != null && Number.isFinite(knownValue) && knownValue > 0) {
    annualKwh = mode === "bill" ? (knownValue / UNIT_RATE_PENCE) * 100 : knownValue;
  } else {
    annualKwh = USAGE_BAND_KWH[size];
  }
  const annualCost = (annualKwh * UNIT_RATE_PENCE) / 100;

  // Usage decides how big a system would be worth having; ROOF_MAX_KW[size] decides
  // how big a system that household could actually fit. Household size no longer
  // scales the "worth having" figure itself (that depends on usage alone, so the same
  // entered usage/bill always starts from the same place), but it still caps the
  // result, so the two inputs genuinely work together rather than one overriding the
  // other outright.
  const usageDrivenKw = annualKwh * KWP_PER_KWH_USAGE;
  const roofCapped = usageDrivenKw > ROOF_MAX_KW[size];
  const systemKw = roundToHalfKw(Math.min(Math.max(usageDrivenKw, MIN_SYSTEM_KW), ROOF_MAX_KW[size]));
  const generationKwh = systemKw * BASE_YIELD_KWH_PER_KWP * yieldMultiplierForPostcode(postcode);

  // Can't self-consume more than the home actually uses in a year.
  const selfUsedKwh = Math.min(generationKwh * SELF_CONSUMPTION_SHARE, annualKwh);
  const exportedKwh = Math.max(generationKwh - selfUsedKwh, 0);

  const billSaving = (selfUsedKwh * UNIT_RATE_PENCE) / 100;
  const exportIncome = (exportedKwh * SEG_RATE_PENCE) / 100;
  const totalSaving = billSaving + exportIncome;
  const savingPct = annualKwh > 0 ? (selfUsedKwh / annualKwh) * 100 : 0;
  const selfConsumptionShare = generationKwh > 0 ? selfUsedKwh / generationKwh : 0;

  return {
    annualKwh,
    annualCost,
    systemKw,
    generationKwh,
    selfUsedKwh,
    exportedKwh,
    totalSaving,
    savingPct,
    selfConsumptionShare,
    roofCapped,
  };
}
